import { useAuth } from '@xhubio-saas/core-frontend/kernel'
import { resolveLocale, resolveOrgLocale } from '@xhubio-saas/country-specifics-frontend'
import { useTranslation } from 'react-i18next'
import { trpc } from '@/trpc'

type ResolveLocaleCountryCode = NonNullable<Parameters<typeof resolveLocale>[0]['countryCode']>

/**
 * BCP 47 tags this app ships `Intl.DateTimeFormat` / `Intl.NumberFormat`
 * rules for.
 */
const AVAILABLE_LOCALES = ['de-DE', 'de-AT', 'de-CH', 'fr-FR', 'fr-CH', 'en']

/**
 * Resolves the active formatter locale.
 *
 * Priority (per resolveLocale):
 *   1. user UI language (i18next) — so a German UI gets German dates
 *      even when the org context isn't loaded yet (e.g., pending deletion)
 *   2. org country default
 *   3. hardFallback "en"
 *
 * `user-preferred locale` and `org-primary language` will plug in here once
 * the backend exposes them.
 */
export function useOrgLocale(): string {
  const { i18n } = useTranslation()
  // Only ask for the org context when authenticated. On public routes (login,
  // /invite/accept, …) the visitor has no session, so this protected query would
  // 401 and surface a misleading "You must be logged in" toast. Logged out, the
  // resolver falls back to the UI language anyway (see priority order above).
  const { isAuthenticated } = useAuth()
  const orgQuery = trpc.organization.current.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  })
  const countryCode = orgQuery.data?.countryCode ?? undefined

  return resolveLocale({
    userPreference: i18n.language,
    countryCode: countryCode as ResolveLocaleCountryCode | undefined,
    availableLocales: AVAILABLE_LOCALES,
  })
}

/**
 * Massystem des Betriebs — `metric` (weltweiter Normalfall) oder `imperial`.
 *
 * Eigene Funktion statt eines zweiten Rueckgabewerts an `useOrgLocale`: dessen
 * Signatur ist `string` und wird an vielen Stellen so benutzt. Die Query ist
 * dieselbe wie oben (gleicher Key, gleiche `staleTime`) — React Query
 * dedupliziert sie, es entsteht kein zweiter Request.
 *
 * Die Aufloesung liegt bewusst im zentralen Resolver und nicht hier: ein
 * US-Betrieb ohne ausdrueckliche Einstellung ist imperial, ohne dass diese App
 * die Laendertabelle ein zweites Mal nachbaut. Genau so macht es dashandwerk
 * in `useOrgFormat`.
 */
export function useOrgMeasurementSystem(): 'metric' | 'imperial' {
  const { isAuthenticated } = useAuth()
  const orgQuery = trpc.organization.current.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  })
  return resolveOrgLocale(orgQuery.data?.countryCode ?? undefined, {
    measurementSystem: orgQuery.data?.measurementSystem,
  }).measurementSystem
}
